import React, { useState } from 'react';

/**
 * Book-a-demo / Contact-sales lead form → eng-1's PUBLIC pipe:
 *   POST /api/demo-request { email, company, message? } → 200 {ok} / 400 {error}
 *
 * ONE lead-capture pipe (PM): the landing's book-a-demo AND the plans-page Enterprise
 * "Contact sales" both funnel here. This is the in-app (agent.biombo.app) instance, shown as a
 * modal; the marketing landing (biombo-landing, separate repo) mirrors this form against the
 * same endpoint. Validation mirrors the server: email required+valid, company 1..200,
 * message optional ≤2000.
 */
export const DemoRequestForm: React.FC<{
  open: boolean;
  onClose: () => void;
  heading?: string;
  blurb?: string;
}> = ({ open, onClose, heading = 'Talk to sales', blurb = "Tell us about your team and we'll be in touch." }) => {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company, message: message || undefined }),
      });
      if (res.ok) {
        setStatus('done');
      } else {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Could not send your request. Please check your details and retry.');
        setStatus('error');
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-nav/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-surface w-full max-w-md rounded-3xl p-8 border border-border-neutral shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {status === 'done' ? (
          <div className="text-center">
            <div className="text-4xl mb-3">✅</div>
            <h2 className="text-2xl font-bold mb-2">Thanks — we'll be in touch</h2>
            <p className="text-gray-400 mb-6">Our team will reach out to {email} shortly.</p>
            <button onClick={onClose} className="w-full py-3 rounded-2xl bg-primary text-white font-bold hover:bg-primary-dark">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-2xl font-bold">{heading}</h2>
              <button type="button" onClick={onClose} aria-label="Close" className="text-text-muted hover:text-text-strong text-xl leading-none">
                ✕
              </button>
            </div>
            <p className="text-gray-400 mb-6">{blurb}</p>
            <div className="space-y-4">
              <input
                required
                type="email"
                placeholder="Work email"
                className="w-full bg-canvas border border-border-neutral rounded-xl px-4 py-3 text-text-strong focus:border-primary outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                required
                maxLength={200}
                placeholder="Company"
                className="w-full bg-canvas border border-border-neutral rounded-xl px-4 py-3 text-text-strong focus:border-primary outline-none"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              <textarea
                rows={3}
                maxLength={2000}
                placeholder="Anything we should know? (optional)"
                className="w-full bg-canvas border border-border-neutral rounded-xl px-4 py-3 text-text-strong focus:border-primary outline-none resize-none"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-3 rounded-2xl bg-primary text-white font-bold hover:bg-primary-dark disabled:opacity-50"
              >
                {status === 'sending' ? 'Sending…' : 'Request a demo'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DemoRequestForm;
